import { useReducer, useCallback } from 'react';
import type { DashboardPolicy, PolicyRule } from './types';

// Deployment wizard state
export interface DeploymentWizardState {
  // Step tracking
  step: 'tenant-selection' | 'rule-selection' | 'summary' | 'closed';
  
  // Policy being deployed
  policy: DashboardPolicy | null;
  
  // Tenant selection
  selectedTenantIds: Set<string>;
  
  // Rule selection per tenant: Map<tenantId, Set<ruleId>>
  tenantRuleSelections: Map<string, Set<string>>;
  
  // Active tenant in rule selection step
  activeTenantId: string;
  
  // Expanded rules in UI
  expandedRuleIds: Set<string>;
  
  // Validation errors
  error: string;
}

// Actions
type DeploymentWizardAction =
  | { type: 'START_DEPLOYMENT'; payload: { policy: DashboardPolicy } }
  | { type: 'CLOSE_WIZARD' }
  | { type: 'GO_TO_RULE_SELECTION'; payload: { selectedTenantIds: Set<string> } }
  | { type: 'GO_TO_SUMMARY' }
  | { type: 'GO_BACK_TO_TENANT_SELECTION' }
  | { type: 'GO_BACK_TO_RULE_SELECTION' }
  | { type: 'SET_ACTIVE_TENANT'; payload: { tenantId: string } }
  | { type: 'TOGGLE_RULE'; payload: { tenantId: string; ruleId: string } }
  | { type: 'SELECT_ALL_RULES_FOR_TENANT'; payload: { tenantId: string; ruleIds: string[] } }
  | { type: 'DESELECT_ALL_RULES_FOR_TENANT'; payload: { tenantId: string } }
  | { type: 'TOGGLE_RULE_EXPAND'; payload: { ruleId: string } }
  | { type: 'SET_ERROR'; payload: { error: string } }
  | { type: 'CLEAR_ERROR' }
  | { type: 'RESET' };

// Initial state
const initialState: DeploymentWizardState = {
  step: 'closed',
  policy: null,
  selectedTenantIds: new Set(),
  tenantRuleSelections: new Map(),
  activeTenantId: '',
  expandedRuleIds: new Set(),
  error: '',
};

// Reducer
function deploymentWizardReducer(
  state: DeploymentWizardState,
  action: DeploymentWizardAction
): DeploymentWizardState {
  switch (action.type) {
    case 'START_DEPLOYMENT':
      return {
        ...initialState,
        step: 'tenant-selection',
        policy: action.payload.policy,
        error: '',
      };

    case 'CLOSE_WIZARD':
      return initialState;

    case 'GO_TO_RULE_SELECTION': {
      const { selectedTenantIds } = action.payload;
      const firstTenant = Array.from(selectedTenantIds)[0] || '';
      
      // Initialize rule selections for each tenant (all rules selected by default)
      const tenantRuleSelections = new Map<string, Set<string>>();
      if (state.policy) {
        const allRuleIds = new Set(state.policy.rules.map(r => r.ruleId));
        selectedTenantIds.forEach(tenantId => {
          tenantRuleSelections.set(tenantId, new Set(allRuleIds));
        });
      }
      
      return {
        ...state,
        step: 'rule-selection',
        selectedTenantIds,
        tenantRuleSelections,
        activeTenantId: firstTenant,
        error: '',
      };
    }

    case 'GO_TO_SUMMARY':
      return {
        ...state,
        step: 'summary',
        error: '',
      };

    case 'GO_BACK_TO_TENANT_SELECTION':
      return {
        ...state,
        step: 'tenant-selection',
        error: '',
      };

    case 'GO_BACK_TO_RULE_SELECTION':
      return {
        ...state,
        step: 'rule-selection',
        error: '',
      };

    case 'SET_ACTIVE_TENANT':
      return {
        ...state,
        activeTenantId: action.payload.tenantId,
        expandedRuleIds: new Set(), // Reset expanded rules when switching tenants
      };

    case 'TOGGLE_RULE': {
      const { tenantId, ruleId } = action.payload;
      const newSelections = new Map(state.tenantRuleSelections);
      const currentRules = newSelections.get(tenantId) || new Set();
      const updatedRules = new Set(currentRules);
      
      if (updatedRules.has(ruleId)) {
        updatedRules.delete(ruleId);
      } else {
        updatedRules.add(ruleId);
      }
      
      newSelections.set(tenantId, updatedRules);
      
      return {
        ...state,
        tenantRuleSelections: newSelections,
      };
    }

    case 'SELECT_ALL_RULES_FOR_TENANT': {
      const { tenantId, ruleIds } = action.payload;
      const newSelections = new Map(state.tenantRuleSelections);
      newSelections.set(tenantId, new Set(ruleIds));
      
      return {
        ...state,
        tenantRuleSelections: newSelections,
      };
    }

    case 'DESELECT_ALL_RULES_FOR_TENANT': {
      const { tenantId } = action.payload;
      const newSelections = new Map(state.tenantRuleSelections);
      newSelections.set(tenantId, new Set());
      
      return {
        ...state,
        tenantRuleSelections: newSelections,
      };
    }

    case 'TOGGLE_RULE_EXPAND': {
      const { ruleId } = action.payload;
      const newExpanded = new Set(state.expandedRuleIds);
      
      if (newExpanded.has(ruleId)) {
        newExpanded.delete(ruleId);
      } else {
        newExpanded.add(ruleId);
      }
      
      return {
        ...state,
        expandedRuleIds: newExpanded,
      };
    }

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload.error,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: '',
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

// Hook
export function useDeploymentWizard() {
  const [state, dispatch] = useReducer(deploymentWizardReducer, initialState);

  const startDeployment = useCallback((policy: DashboardPolicy) => {
    dispatch({ type: 'START_DEPLOYMENT', payload: { policy } });
  }, []);

  const closeWizard = useCallback(() => {
    dispatch({ type: 'CLOSE_WIZARD' });
  }, []);

  const goToRuleSelection = useCallback((selectedTenantIds: Set<string>) => {
    if (selectedTenantIds.size === 0) {
      dispatch({ type: 'SET_ERROR', payload: { error: 'Please select at least one tenant' } });
      return false;
    }
    dispatch({ type: 'GO_TO_RULE_SELECTION', payload: { selectedTenantIds } });
    return true;
  }, []);

  const goToSummary = useCallback(() => {
    // Validate that at least one rule is selected for each tenant
    const hasInvalidSelection = Array.from(state.tenantRuleSelections.entries()).some(
      ([, rules]) => rules.size === 0
    );
    
    if (hasInvalidSelection) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: { error: 'Please select at least one rule for each tenant' } 
      });
      return false;
    }
    
    dispatch({ type: 'GO_TO_SUMMARY' });
    return true;
  }, [state.tenantRuleSelections]);

  const goBackToTenantSelection = useCallback(() => {
    dispatch({ type: 'GO_BACK_TO_TENANT_SELECTION' });
  }, []);

  const goBackToRuleSelection = useCallback(() => {
    dispatch({ type: 'GO_BACK_TO_RULE_SELECTION' });
  }, []);

  const setActiveTenant = useCallback((tenantId: string) => {
    dispatch({ type: 'SET_ACTIVE_TENANT', payload: { tenantId } });
  }, []);

  const toggleRule = useCallback((tenantId: string, ruleId: string) => {
    dispatch({ type: 'TOGGLE_RULE', payload: { tenantId, ruleId } });
  }, []);

  const selectAllRulesForTenant = useCallback((tenantId: string, ruleIds: string[]) => {
    dispatch({ type: 'SELECT_ALL_RULES_FOR_TENANT', payload: { tenantId, ruleIds } });
  }, []);

  const deselectAllRulesForTenant = useCallback((tenantId: string) => {
    dispatch({ type: 'DESELECT_ALL_RULES_FOR_TENANT', payload: { tenantId } });
  }, []);

  const toggleRuleExpand = useCallback((ruleId: string) => {
    dispatch({ type: 'TOGGLE_RULE_EXPAND', payload: { ruleId } });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    state,
    actions: {
      startDeployment,
      closeWizard,
      goToRuleSelection,
      goToSummary,
      goBackToTenantSelection,
      goBackToRuleSelection,
      setActiveTenant,
      toggleRule,
      selectAllRulesForTenant,
      deselectAllRulesForTenant,
      toggleRuleExpand,
      clearError,
      reset,
    },
  };
}
